"use client"

import * as React from "react"
import { ChevronsUpDown, Plus, Command } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { useTeam } from "@/components/team-context"

export function TeamSwitcher({
  teams,
}: {
  teams: {
    name: string
    logo: React.ElementType
    logoName: string
    plan: string
  }[]
}) {
  const { isMobile } = useSidebar()
  const [isClient, setIsClient] = React.useState(false)
  const { activeTeam, setActiveTeam } = useTeam()

  React.useEffect(() => {
    setIsClient(true)
  }, [])

  React.useEffect(() => {
    // 只在没有 activeTeam 或者 activeTeam 不在当前 teams 列表中时才设置
    if (teams.length > 0 && (!activeTeam || !teams.find(team => team.name === activeTeam.name))) {
      const savedTeam = localStorage.getItem('activeTeam')
      if (savedTeam) {
        try {
          const parsed = JSON.parse(savedTeam)
          const foundTeam = teams.find(team => team.name === parsed.name)
          if (foundTeam && foundTeam.name !== activeTeam?.name) {
            setActiveTeam(foundTeam)
            return
          }
        } catch (e) {
          console.error('Failed to parse saved team:', e)
        }
      }
      // 优先选择非 'kinet' 的真实组织，如果都是 'kinet' 则选择第一个
      if (!activeTeam) {
        const realTeam = teams.find(team => team.name !== 'kinet')
        setActiveTeam(realTeam || teams[0])
      }
    }
  }, [teams.length, activeTeam?.name]) // 只依赖长度和名称，避免对象引用问题

  const handleTeamChange = (team: typeof teams[0]) => {
    setActiveTeam(team)
    localStorage.setItem('activeTeam', JSON.stringify(team))
  }

  if (!isClient || !activeTeam) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
              <Command className="size-4" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">Loading...</span>
              <span className="truncate text-xs">...</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    )
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                {React.createElement(activeTeam.logo, { className: "size-4" })}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {activeTeam.name}
                </span>
                <span className="truncate text-xs">{activeTeam.plan}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Teams
            </DropdownMenuLabel>
            {teams.map((team, index) => (
              <DropdownMenuItem
                key={team.name}
                onClick={() => handleTeamChange(team)}
                className="gap-2 p-2"
              >
                <div className="flex size-6 items-center justify-center rounded-sm border">
                  <team.logo className="size-4 shrink-0" />
                </div>
                {team.name}
                <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem className="gap-2 p-2">
              <div className="flex size-6 items-center justify-center rounded-md border bg-background">
                <Plus className="size-4" />
              </div>
              <div className="font-medium text-muted-foreground">Add team</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
